import { action, runInAction, makeObservable } from 'mobx';
import InformacoesProcessoApi, { FeedbackAguiaPayload } from '../api/InformacoesProcessoApi';
import MinutaApi from '../api/MinutaApi';
import ProcessoApi from '../api/ProcessoApi';
import { UtilData } from '../misc/UtilData';
import { IEtiqueta, Minuta, Processo } from '../modelo';
// import { Alerta } from '../modelo/dominio/Alertas';
import { ClasseJudicial } from '../modelo/dominio/ClasseJudicial';
import { DadosProcesso } from '../modelo/dominio/DadosProcesso';
import { OrgaoJulgador } from '../modelo/dominio/OrgaoJulgador';
import { Partes } from '../modelo/dominio/Partes';
import { Relator } from '../modelo/dominio/Relator';
import { TipoPeca } from '../modelo/dominio/TipoPeca';
import { AlertaDto } from '../modelo/dto/AlertaDto';
import { DadosProcessoDto } from '../modelo/dto/DadosProcessoDto';
import { PartesDto } from '../modelo/dto/PartesDto';
import ProcessoExpandidoStore from '../store/ProcessoExpandidoStore';
import ProcessoStore from '../store/ProcessoStore';
import FiltroStore from '../store/FiltroStore';
import { Peca } from './../modelo/dominio/Peca';
import { PecaDto } from './../modelo/dto/PecaDto';
import { ErrorUtils } from './../utils/ErrorUtils';
import { extrairApiErroPadrao } from '../utils/ApiErroPadraoUtils';
import AssuntoApi from '../api/AssuntoApi';
import NumeroProcesso from '@/utils/NumeroProcesso';
import Configuracao from '@/misc/Configuracao';

class ProcessoExpandidoService {
    constructor() {
        makeObservable(this, {
            handleChangePainelExpandido: action
        });
    }

    async consultarDadosProcesso(idProcesso: number, completarListaDeProcessos: boolean = true): Promise<void> {
        const processo: Processo = ProcessoStore.processosAgrupados.find(
            (proc) => proc.identificadorProcesso === idProcesso
        );

        const indicadoresDto = completarListaDeProcessos && processo?.indicadores;
        const [dadosProcessoDto, alertasDto, tarefaFluxoDto, impedimentosDto] =
            await Promise.all([
                InformacoesProcessoApi.consultarProcesso(idProcesso).catch((err) => new Error(err)),
                InformacoesProcessoApi.consultarAlertasProcesso(idProcesso).catch((err) => new Error(err)),
                InformacoesProcessoApi.consultarTarefaFluxoPJe(idProcesso).catch((err) => new Error(err)),
                ProcessoApi.buscarImpedimentosNoProcesso(idProcesso).catch((err) => new Error(err))
            ]);

        const partialDadosProcesso: Partial<DadosProcesso> = ErrorUtils.ifSuccess(dadosProcessoDto, mapDadosProcesso, {});
        partialDadosProcesso.idProcesso = idProcesso;
        partialDadosProcesso.origem = partialDadosProcesso.origem || processo?.origemProcesso?.id;
        partialDadosProcesso.alertas = ErrorUtils.ifSuccess(alertasDto, (alertas) => alertas?.map((alerta) => AlertaDto.novo(alerta)), []);
        partialDadosProcesso.tarefaFluxo = ErrorUtils.ifSuccess(tarefaFluxoDto, (tarefaFluxo) => ({ tarefaAtual: tarefaFluxo.tarefaAtual }));
        partialDadosProcesso.indicadores = ErrorUtils.ifSuccess(indicadoresDto, (indicadores) => indicadores && indicadores.map((indicador) => ({
            descricao: indicador.descricao,
            isEtiqueta: indicador.isEtiqueta
        })), []);
        partialDadosProcesso.impedimentos = ErrorUtils.ifSuccess(impedimentosDto, (impedimentos) => impedimentos);

        if (processo?.assuntosProcesso) {
            partialDadosProcesso.assuntos = processo.assuntosProcesso;
        } else {
            AssuntoApi.buscarPorProcessos([idProcesso]).then(assuntoDto => {
                partialDadosProcesso.assuntos = assuntoDto[idProcesso];
            });
        }

        const possuiPartesCadastradas = (partes: any): boolean => {
            if (!partes || Array.isArray(partes)) {
                return Array.isArray(partes) && partes.length > 0;
            }
            return ['ATIVO', 'PASSIVO', 'TERCEIROS']
                .some((polo) => Array.isArray(partes[polo]) && partes[polo].length > 0);
        };

        const ehProcessoLegado = partialDadosProcesso.origem === 'L';
        let deveCarregarPartes = false;
        if (!partialDadosProcesso.partes && processo?.partes
            && (!ehProcessoLegado || possuiPartesCadastradas(processo.partes))) {
            partialDadosProcesso.partes = processo.partes;
        } else if (!partialDadosProcesso.partes
            || (ehProcessoLegado && !possuiPartesCadastradas(partialDadosProcesso.partes))) {
            partialDadosProcesso.partes = undefined;
            deveCarregarPartes = true;
        }

        if (processo) {
            runInAction(() => {
                processo.impedimentos = partialDadosProcesso.impedimentos;
                if (FiltroStore.filtroAplicado) {
                    processo.resetMostrar();
                    FiltroStore.aplicarFiltros(processo);
                }
            });
        }

        const dadosProcesso = DadosProcesso.novo(partialDadosProcesso);
        ProcessoExpandidoStore.setDadosProcesso(dadosProcesso);
        if (deveCarregarPartes) {
            void this.carregarPartes(
                processo?.identificadorProcesso || partialDadosProcesso.identificadorProcesso || partialDadosProcesso.idProcesso
            ).catch(() => undefined);
        }
    }

    async consultarPecas(idProcesso: number): Promise<void> {
        ProcessoExpandidoStore.iniciarCarregamentoPecas(idProcesso);
        try {
            const [dadosPecas, dadosPeticoesPendentes] = await Promise.all([
                InformacoesProcessoApi.consultarDocumentosProcesso(idProcesso).catch((err) => new Error(err)),
                InformacoesProcessoApi.consultarPeticoesPendentes(idProcesso).catch((err) => new Error(err)),
            ]);

            if (ProcessoExpandidoStore.idProcessoCarregandoPecas !== idProcesso) {
                return;
            }

            const peticoesPendentes = ErrorUtils.ifSuccess(dadosPeticoesPendentes, (dados) => dados, []).map((peticao) => peticao);

            // CodTiposDocsPeticaoPendente
            // 1,4,5,6,8,10,11,13,19,20,21,22,23,24,28,35,36,38,40,41,42,44,46,46,47,48,49,66,208,209,211,213,214,310,312,313,388,464,468,469,470,471,472,473,474,475,476,480,482,483,485,487,488,490,491,496

            const pecas = mapPecasProcesso(
                ErrorUtils.ifSuccess(dadosPecas as Error | PecaDto[], (dados) => dados, []),
                peticoesPendentes,
                ProcessoExpandidoStore.dadosProcesso?.origem
            );

            if (ProcessoExpandidoStore.dadosProcesso?.origem == 'L') {
                const numero = NumeroProcesso.valueOf(ProcessoExpandidoStore.dadosProcesso.numero);
                try {
                    const jurisprudencia = await InformacoesProcessoApi.consultarJurisprudencia(numero.getNumero().toString(), numero.getAno().toString(), numero.getDigito().toString(), numero.getJustica().toString(), numero.getTribunal().toString(), numero.getVara().toString());
                    pecas.forEach(peca => {
                        const registroJurisprudencia = jurisprudencia?.registros?.find(registro => registro.registro.numProcDocumento === peca.id);
                        if (registroJurisprudencia) {
                            peca.urlDocumentoHTML = Configuracao.urlDocJurisprudencia.replace("{IDJURISPRUDENCIA}", registroJurisprudencia.registro.id);
                        }
                    });
                } catch (erro) {
                    console.warn('Não foi possível consultar documentos HTML da jurisprudência para o processo legado.', erro);
                }

            }

            if (ProcessoExpandidoStore.idProcessoCarregandoPecas === idProcesso) {
                ProcessoExpandidoStore.setPecas(pecas);
            }
        } finally {
            ProcessoExpandidoStore.finalizarCarregamentoPecas(idProcesso);
        }
    }

    async consultarResumoDocumentosAguiaPublico(idProcesso: number, isSilent: boolean = false) {
        const resumoInformacoes = await InformacoesProcessoApi.consultarResumoDocumentosAguia(idProcesso, isSilent)
        return resumoInformacoes
    }

    async gerarResumoDocumentosAguiaPublico(idProcesso: number) {
        const resumoInformacoes = await InformacoesProcessoApi.gerarResumoDocumentosAguia(idProcesso)
        return resumoInformacoes
    }

    async consultarFeedbackAguiaPublico(idProcesso: number) {
        return InformacoesProcessoApi.consultarFeedbackAguia(idProcesso);
    }

    async gerarFeedbackAguia(idProcesso: number, feedbackAguia: FeedbackAguiaPayload) {
        await InformacoesProcessoApi.gerarFeedbackAguia(idProcesso, feedbackAguia);
    }

    async consultarMinutas(idProcesso: number): Promise<void> {
        const minutas = await MinutaApi.recuperaMinutas(idProcesso).catch(
            (err) => new Error(err)
        );
        const processoPai = ProcessoStore._processos.find(proc => proc.identificadorProcesso === idProcesso);
        runInAction(() => {
            ProcessoExpandidoStore.minutas = [];
            (minutas as Minuta[]).forEach((minuta) => {
                ProcessoExpandidoStore.minutas.push(Minuta.criar(minuta, processoPai));
            });
            return ProcessoExpandidoStore.minutas.slice().sort((m1, m2) => {
                if (m1.dataUltimaAlteracao > m2.dataUltimaAlteracao) {
                    return 1;
                } else {
                    return -1;
                }
            });
        });
    }

    async consultarMinutasArquivadas(idProcesso: number): Promise<void> {
        const minutas = await MinutaApi.recuperaMinutasArquivadas(idProcesso).catch(
            (err) => new Error(err)
        );
        const processoPai = ProcessoStore._processos.find(proc => proc.identificadorProcesso === idProcesso);
        runInAction(() => {
            ProcessoExpandidoStore.minutasArquivadas = [];
            (minutas as Minuta[]).forEach((minuta) => {
                ProcessoExpandidoStore.minutasArquivadas.push(Minuta.criar(minuta, processoPai));
            });
            return [...new Set(ProcessoExpandidoStore.minutasArquivadas.slice().sort((m1, m2) => {
                if (m1.dataUltimaAlteracao > m2.dataUltimaAlteracao) {
                    return 1;
                } else {
                    return -1;
                }
            }))];
        });
    }

    async consultarMinutasAssinadas(idProcesso: number): Promise<void> {
        const minutas = await MinutaApi.recuperaMinutasAssinadas(idProcesso).catch(
            (err) => new Error(err)
        );

        runInAction(() => {
            ProcessoExpandidoStore.minutasAssinadas = [];
            const processoPai = ProcessoStore._processos.find(proc => proc.identificadorProcesso === idProcesso);
            (minutas as Minuta[]).forEach((minuta) => {
                ProcessoExpandidoStore.minutasAssinadas.push(Minuta.criar(minuta, processoPai));
            });
            return ProcessoExpandidoStore.minutasAssinadas.slice().sort((m1, m2) => {
                if (m1.dataUltimaAlteracao > m2.dataUltimaAlteracao) {
                    return 1;
                } else {
                    return -1;
                }
            });
        });
    }

    async carregarPartes(idProcesso: number) {
        try {
            const partes: Map<number, PartesDto> = await InformacoesProcessoApi.consultarPartesDaListaDeProcesso([idProcesso]);
            runInAction(() => {
                if (ProcessoExpandidoStore.dadosProcesso?.idProcesso === idProcesso) {
                    ProcessoExpandidoStore.dadosProcesso.partes = partes.get(idProcesso);
                    ProcessoExpandidoStore.dadosProcesso.mensagemErroPartes = undefined;
                }
            });
        } catch (erro) {
            const apiErro = extrairApiErroPadrao(erro);
            const mensagem = [apiErro?.message, apiErro?.details]
                .filter((texto) => !!texto?.trim())
                .join('\n');
            runInAction(() => {
                if (ProcessoExpandidoStore.dadosProcesso?.idProcesso === idProcesso) {
                    ProcessoExpandidoStore.dadosProcesso.partes = undefined;
                    ProcessoExpandidoStore.dadosProcesso.mensagemErroPartes =
                        ProcessoExpandidoStore.dadosProcesso.origem === 'L'
                            ? mensagem || 'Não foi possível carregar as partes do processo.'
                            : undefined;
                }
            });
            throw erro;
        }
    }

    async consultarEtiquetas(idProcesso: number): Promise<void> {
        const etiquetas = await ProcessoApi.recuperarEtiquetas(idProcesso);
        runInAction(() => {
            const processo = ProcessoStore._processos.find(proc => proc.identificadorProcesso === idProcesso);
            runInAction(() => {
                processo.etiquetas = etiquetas;
            });
        });
    }

    handleChangePainelExpandido(processo: Processo) {
        if (processo.idProcessoEscaninho === ProcessoExpandidoStore.idProcessoPainelExpandido)
            ProcessoExpandidoStore.idProcessoPainelExpandido = '';
        else {
            ProcessoExpandidoStore.abaSelecionada = 0;
            ProcessoExpandidoStore.idProcessoPainelExpandido = processo.idProcessoEscaninho;
            ProcessoExpandidoStore.limparPecas();
        }
    }
}

export function mapPecasProcesso(dadosPecas: PecaDto[], peticoesPendentes: PecaDto[], origem?: string): Peca[] {
    return dadosPecas.map((dto, index, array) => {
        const pendenciasDaPeca = peticoesPendentes.filter(
            peticao => peticao.idUnicoDocumento == dto.idUnicoDocumento
        );
        return mapPeca(dto, array.length - index, pendenciasDaPeca, origem);
    });
}

function mapPeca(dto: PecaDto, numeroOrdem: number, peticoesPendentes: PecaDto[], origem?: string): Peca {
    const codigos = [1, 4, 5, 6, 8, 10, 11, 13, 19, 20, 21, 22, 23, 24, 28, 35, 36, 38, 40, 41, 42, 44, 46, 46, 47, 48, 49, 66, 208, 209, 211, 213, 214, 310, 312, 313, 388, 464, 468, 469, 470, 471, 472, 473, 474, 475, 476, 480, 482, 483, 485, 487, 488, 490, 491, 496];

    const existePeticaoPendente = peticoesPendentes.length > 0;

    const pecaDTO = Object.assign(dto, {
        t: existePeticaoPendente,
        numeroOrdem,
        juntadoEm: dto.juntadoEm && new Date(UtilData.formataData(dto.juntadoEm, true)),
        criadoEm: dto.criadoEm && new Date(UtilData.formataData(dto.criadoEm, true)),
        tipoPeca: dto.tipo && new TipoPeca(dto.tipo.nomeTipoProcessoDocumento),
        codigoPeticaoStatus: null,
        descricaoPeticaoStatus: null,
        peticaoNaoLida: null
    });

    pecaDTO.peticaoNaoLida = existePeticaoPendente ? true : false;

    if (origem == 'P') {
        if (codigos.includes(dto.tipo.id)) {
            pecaDTO.descricaoPeticaoStatus = 'Apreciada';
        }
        if (peticoesPendentes[0]?.apreciado == false) {
            pecaDTO.descricaoPeticaoStatus = 'Não apreciada';
        }
    } else if (origem == 'L') {
        pecaDTO.codigoPeticaoStatus = peticoesPendentes[0] != null ? (peticoesPendentes[0]?.codigoPeticaoStatus != null ? peticoesPendentes[0]?.codigoPeticaoStatus : null) : null;
        pecaDTO.descricaoPeticaoStatus = peticoesPendentes[0] != null ? (peticoesPendentes[0]?.descricaoPeticaoStatus != null ? peticoesPendentes[0]?.descricaoPeticaoStatus : (peticoesPendentes[0]?.apreciado ? 'Apreciada' : 'Não apreciada')) : null;
    }

    return Peca.novo(pecaDTO);
}

function mapDadosProcesso(dadosProcesso: DadosProcessoDto): Partial<DadosProcesso> {
    return {
        numero: dadosProcesso.numero,
        classeJudicial: dadosProcesso.classesJudicial && ClasseJudicial.novoComArray(dadosProcesso.classesJudicial),
        relator: dadosProcesso.relator && Relator.novo(dadosProcesso.relator),
        orgaoJulgador: dadosProcesso.orgaoJulgador && OrgaoJulgador.novo(dadosProcesso.orgaoJulgador),
        orgaoJulgadorCargo: dadosProcesso.orgaoJulgadorCargo,
        orgaoJulgadorColegiado: dadosProcesso.orgaoJulgadorColegiado && OrgaoJulgador.novo(dadosProcesso.orgaoJulgadorColegiado),
        dataAutuacao: dadosProcesso.autuadoEm && new Date(dadosProcesso.autuadoEm),
        origem: dadosProcesso.origem,
        sigUnAdm: dadosProcesso.sigUnAdm
    }
}

export default new ProcessoExpandidoService();
